<script>
const columnColours = [
  '#CCF3F9',
  '#95E7F3',
  '#5EDAED',
  '#27CDE7',
  '#15A5BB',
  '#108192',
  '#0C5C68',
  '#07383F',
  '#021316',
]

export default {
  name: "ForecastSummaryChart",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    forecast: {
      type: Object,
      default: null,
    },
    forVersion: {
      type: Number,
      default: null,
    },
    interventionRate: {
      type: Number,
      default: 100,
    },
  },
  computed: {
    hasForecast() {
      return this.forecast && Object.keys(this.forecast).length > 0;
    },

    currentMonth() {
      return this.forecast.current?.month;
    },

    currentSubmitted() {
      return this.forecast.current?.submitted;
    },

    currentRevision() {
      return this.forVersion || this.forecast.current.revision;
    },

    initials() {
      return this.forecast.initials || {};
    },

    actuals() {
      if (this.currentRevision == 1 && !this.currentSubmitted) {
        return this.initials;
      }
      return this.forecast.actuals || {};
    },

    previousRevision() {
      if (this.currentRevision <= 1) {
        return {};
      }

      return this.forecast.revisions[this.currentRevision - 1] || {};
    },

    revisions() {
      return this.forecast.revisions[this.currentRevision] || {};
    },

    mergedData() {
      if (!this.currentSubmitted && this.currentRevision == 1) {
        return Object.assign({}, this.initials, this.actuals);
      }
      return Object.assign({}, this.initials, this.actuals, this.revisions);
    },

    intervals() {
      return Object.keys(this.forecast.initials || {});
    },

    categories() {
      return this.initials[this.intervals[0]];
    },

    chartData() {
      return {
        labels: this.intervals,
        datasets: [{
          yAxisID: 'totals',
          label: "Initials",
          borderColor: "#99cc33",
          backgroundColor: '#99cc33',
          fill: false,
          type: "line",
          data: this.intervals.map(interval => {
            const data = this.initials[interval];
            return data ? this.calculateData(data) : null
          }),
        },
        {
          yAxisID: 'totals',
          label: "Actuals",
          borderColor: "#000000",
          backgroundColor: '#000000',
          fill: false,
          type: "line",
          data: this.intervals.map(interval => {
            if(interval > this.currentMonth) {
              return null;
            }
            const data = this.actuals[interval];
            return data ? this.calculateData(data) : null
          }),
        },
        {
          yAxisID: 'totals',
          label: "Projection",
          borderColor: "#444",
          backgroundColor: '#444',
          fill: false,
          borderDash: [5, 5],
          type: "line",
          data: this.intervals.map(interval => {
            const data = this.previousRevision?.[interval];
            return data ? this.calculateData(data) : null
          }),
        }, ...this.categories.map((cat, index) => {
          const data = this.intervals.map(interval => {
            const d = this.mergedData[interval].find(i => i.key === cat.key);
            return d.value * (this.interventionRate / 100)
          });
          return {
            label: cat.name,
            backgroundColor: columnColours[index],
            data,
          }
        })],
      }
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        scales: {
          xAxes: [{
            stacked: true,
          }],
          yAxes: [{
            id: 'values',
            type: 'linear',
            stacked: true,
            ticks: {
              beginAtZero: true,
              callback: (value) => {
                return value?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
              },
            },
          },
          {
            id: 'totals',
            type: 'linear',
            position: 'right',
            gridLines: {
              drawOnChartArea: false,
            },
            ticks: {
              beginAtZero: true,
              callback: (value) => {
                return value?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
              },
            },
          }],
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (tooltipItems) => {
              const interval = tooltipItems[0].xLabel;
              if(interval === this.currentMonth) {
                return `${interval} (current month)`;
              }
              return `${interval}`;
            },
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const value = dataset.data[tooltipItem.index] ?? 0;
              return `${dataset.label}: ${value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}`;
            },
          },
        },
      };
    }
  },
  methods: {
    calculateData(data) {
      const values = data.map(cat => cat.value)
      if (values.some(v => v == null)) {
        return null;
      }
      return values.reduce((total, value) => total + (value || 0), 0) * (this.interventionRate / 100);
    }
  },
}
</script>

<template>
  <div
    v-if="hasForecast"
    :class="['container', embedded ? '' : 'mt-12']"
  >
    <h2 v-if="!embedded">
      Financial Forecast
    </h2>
    <column-chart
      :chart-data="chartData"
      :options="chartOptions"
      class="w-full"
      style="height: 24rem"
    />
  </div>
</template>
